// The command library: every edit to a Design document is one of these (DRAW_PLAN §4). Each returns a
// Command whose recipe mutates an Immer draft; history.ts records patches for undo.
import type { Command } from './history';
import type {
  Appearance,
  Artboard,
  ColorRef,
  Doc,
  Layer,
  Mat2D,
  Node,
  Paint,
  Shape,
  Subpath,
  Swatch,
} from '../model/types';
import { shapeToSubpaths } from '../geometry/bezier';
import { findNode } from '../model/tree';
import { makeLayer } from '../model/defaults';
import { newId } from '../model/ids';

export const cmd = (label: string, recipe: (d: Doc) => void, merge?: string): Command => ({
  label,
  recipe,
  merge,
});

export const addNode = (node: Node, layerId?: string, index?: number): Command =>
  cmd('cmdAdd', (d) => {
    const layer = d.layers.find((l) => l.id === layerId) ?? d.layers[d.layers.length - 1];
    if (!layer) return;
    if (index === undefined) layer.children.push(node);
    else layer.children.splice(index, 0, node);
  });

export const removeNodes = (ids: string[]): Command =>
  cmd('cmdDelete', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (f) f.siblings.splice(f.index, 1);
    }
  });

/** Edits one node in place (`fn` receives the draft node). `merge` makes a drag one undo step. */
export const updateNode = (id: string, fn: (n: Node) => void, label = 'cmdEdit', merge?: string): Command =>
  cmd(
    label,
    (d) => {
      const f = findNode(d, id);
      if (f) fn(f.node);
    },
    merge,
  );

export const setTransform = (id: string, m: Mat2D, merge?: string): Command =>
  updateNode(id, (n) => void (n.transform = [...m] as Mat2D), 'cmdTransform', merge);

export const renameDoc = (name: string): Command => cmd('cmdRename', (d) => void (d.name = name));

export const addLayer = (name?: string): Command =>
  cmd('cmdAddLayer', (d) => void d.layers.push(makeLayer(name ?? `Layer ${d.layers.length + 1}`)));

export const updateLayer = (id: string, fn: (l: Layer) => void, label = 'cmdLayer'): Command =>
  cmd(label, (d) => {
    const l = d.layers.find((x) => x.id === id);
    if (l) fn(l);
  });

export const removeLayer = (id: string): Command =>
  cmd('cmdDeleteLayer', (d) => {
    if (d.layers.length <= 1) return; // a document always has a layer
    const i = d.layers.findIndex((l) => l.id === id);
    if (i >= 0) d.layers.splice(i, 1);
  });

export const addArtboard = (ab: Omit<Artboard, 'id'>): Command =>
  cmd('cmdAddArtboard', (d) => void d.artboards.push({ ...ab, id: newId('ab') }));

export const updateArtboard = (id: string, fn: (a: Artboard) => void, merge?: string): Command =>
  cmd(
    'cmdArtboard',
    (d) => {
      const a = d.artboards.find((x) => x.id === id);
      if (a) fn(a);
    },
    merge,
  );

export const removeArtboard = (id: string): Command =>
  cmd('cmdDeleteArtboard', (d) => {
    if (d.artboards.length <= 1) return;
    const i = d.artboards.findIndex((a) => a.id === id);
    if (i >= 0) d.artboards.splice(i, 1);
  });

// ── D1: selection, arrange, group, layers ──────────────────────────────────────────────────────────────
// Selection-level commands act on "top" nodes (a layer's direct children), which is what the Selection
// tool selects; nested nodes are edited through their group (Direct selection comes in D3).

/** Sets several nodes' transforms in one step (a drag frame); `merge` makes the gesture one undo step. */
export const transformNodes = (next: Map<string, Mat2D>, merge?: string): Command =>
  cmd(
    'cmdTransform',
    (d) => {
      for (const [id, m] of next) {
        const f = findNode(d, id);
        if (f) f.node.transform = [...m] as Mat2D;
      }
    },
    merge,
  );

export const setNodeFlags = (
  ids: string[],
  p: Partial<{ visible: boolean; locked: boolean; name: string }>,
): Command =>
  cmd(p.visible !== undefined ? 'cmdVisibility' : p.locked !== undefined ? 'cmdLock' : 'cmdRename', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (f) Object.assign(f.node, p);
    }
  });

/** Copies of `ids` placed right above each original, offset by (dx, dy); `newIds` receives the copies' ids. */
export const duplicateNodes = (ids: string[], dx = 0, dy = 0, newIds: string[] = []): Command =>
  cmd('cmdDuplicate', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (!f) continue;
      const copy = reId(JSON.parse(JSON.stringify(f.node)) as Node);
      copy.transform = [...copy.transform] as Mat2D;
      copy.transform[4] += dx;
      copy.transform[5] += dy;
      f.siblings.splice(f.index + 1, 0, copy);
      newIds.push(copy.id);
    }
  });

function reId(n: Node): Node {
  n.id = newId(n.kind === 'group' ? 'g' : 'n');
  if (n.kind === 'group') n.children.forEach(reId);
  return n;
}

/** Groups `ids` (in paint order) at the position of the topmost one; `out[0]` receives the group id. */
export const groupNodes = (ids: string[], out: string[] = []): Command =>
  cmd('cmdGroup', (d) => {
    const found = ids.map((id) => findNode(d, id)).filter((f): f is NonNullable<typeof f> => !!f);
    if (found.length < 1) return;
    // Paint order: layer index, then index in the layer.
    const rank = (f: (typeof found)[number]) => d.layers.indexOf(f.layer) * 1e6 + f.index;
    found.sort((a, b) => rank(a) - rank(b));
    const top = found[found.length - 1]!;
    const g: Node = {
      id: newId('g'),
      kind: 'group',
      children: found.map((f) => f.node),
      visible: true,
      locked: false,
      transform: [1, 0, 0, 1, 0, 0],
      appearance: { fills: [], strokes: [], opacity: 1, blend: 'normal', effects: [] },
    };
    const layer = top.layer;
    const at = top.index;
    // Remove from the back so indices stay valid.
    for (const f of [...found].sort((a, b) => rank(b) - rank(a))) f.siblings.splice(f.index, 1);
    const removedBefore = found.filter((f) => f.layer === layer && f.index < at).length;
    layer.children.splice(at - removedBefore, 0, g);
    out.push(g.id);
  });

/** Replaces each group in `ids` by its children (their transforms absorb the group's). */
export const ungroupNodes = (ids: string[], out: string[] = []): Command =>
  cmd('cmdUngroup', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (!f || f.node.kind !== 'group') continue;
      const gm = f.node.transform;
      const kids = f.node.children.map((c) => {
        c.transform = mulMat(gm, c.transform);
        out.push(c.id);
        return c;
      });
      f.siblings.splice(f.index, 1, ...kids);
    }
  });

function mulMat(m: Mat2D, n: Mat2D): Mat2D {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export type ArrangeOp = 'forward' | 'backward' | 'front' | 'back';
/** Bring forward/backward/to front/to back among siblings. */
export const arrangeNodes = (ids: string[], op: ArrangeOp): Command =>
  cmd('cmdArrange', (d) => {
    const found = ids.map((id) => findNode(d, id)).filter((f): f is NonNullable<typeof f> => !!f);
    // Process so that moving one never skips another: front/forward from the top down, back/backward up.
    found.sort((a, b) => (op === 'front' || op === 'forward' ? b.index - a.index : a.index - b.index));
    for (const f0 of found) {
      const f = findNode(d, f0.node.id)!;
      const list = f.siblings,
        i = f.index;
      const [n] = list.splice(i, 1);
      const j =
        op === 'front'
          ? list.length
          : op === 'back'
            ? 0
            : op === 'forward'
              ? Math.min(list.length, i + 1)
              : Math.max(0, i - 1);
      list.splice(j, 0, n!);
    }
  });

/** Moves nodes into a layer (or group) at `index` (layers panel drag and drop). */
export const moveNodesTo = (
  ids: string[],
  target: { layerId: string; groupId?: string },
  index: number,
): Command =>
  cmd('cmdMove', (d) => {
    const nodes: Node[] = [];
    for (const id of ids) {
      if (id === target.groupId) continue;
      const f = findNode(d, id);
      if (!f) continue;
      nodes.push(f.node);
      f.siblings.splice(f.index, 1);
    }
    const layer = d.layers.find((l) => l.id === target.layerId);
    if (!layer) return;
    let list = layer.children;
    if (target.groupId) {
      const g = findNode(d, target.groupId);
      if (g?.node.kind === 'group') list = g.node.children;
    }
    list.splice(Math.min(index, list.length), 0, ...nodes);
  });

/** Moves a layer to `index` in the layer stack. */
export const moveLayer = (id: string, index: number): Command =>
  cmd('cmdLayer', (d) => {
    const i = d.layers.findIndex((l) => l.id === id);
    if (i < 0) return;
    const [l] = d.layers.splice(i, 1);
    d.layers.splice(Math.max(0, Math.min(index, d.layers.length)), 0, l!);
  });

// ── D2: appearance, live shapes, swatches ──────────────────────────────────────────────────────────────

/** Edits the appearance of every node in `ids` (and, for groups, of every drawable inside them). */
export const setAppearance = (ids: string[], fn: (a: Appearance, n: Node) => void, merge?: string): Command =>
  cmd(
    'cmdAppearance',
    (d) => {
      const visit = (n: Node) => {
        if (n.kind === 'group') n.children.forEach(visit);
        else fn(n.appearance, n);
      };
      for (const id of ids) {
        const f = findNode(d, id);
        if (f) visit(f.node);
      }
    },
    merge,
  );

/** Edits a live shape's parameters (corner radii, sides, points …). */
export const setShape = (id: string, fn: (s: Shape) => void, merge?: string): Command =>
  updateNode(
    id,
    (n) => {
      if (n.kind === 'shape') fn(n.shape);
    },
    'cmdShape',
    merge,
  );

/** Expand: live shapes become editable paths with exactly the same outline. */
export const expandShapes = (ids: string[]): Command =>
  cmd('cmdExpand', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (!f || f.node.kind !== 'shape') continue;
      const { shape: _shape, kind: _kind, ...rest } = f.node;
      const path: Node = {
        ...rest,
        kind: 'path',
        subpaths: shapeToSubpaths(f.node.shape),
        fillRule: 'nonzero',
      };
      f.siblings[f.index] = path;
    }
  });

export const addSwatch = (sw: Omit<Swatch, 'id'>, out: string[] = []): Command =>
  cmd('cmdSwatch', (d) => {
    const id = newId('sw');
    d.swatches.push({ ...sw, id });
    out.push(id);
  });
export const updateSwatch = (id: string, fn: (s: Swatch) => void, merge?: string): Command =>
  cmd(
    'cmdSwatch',
    (d) => {
      const s = d.swatches.find((x) => x.id === id);
      if (s) fn(s);
    },
    merge,
  );
/** Deleting a global swatch first turns every use into a plain colour, so nothing changes on the page. */
export const removeSwatch = (id: string): Command =>
  cmd('cmdSwatchDelete', (d) => {
    const i = d.swatches.findIndex((x) => x.id === id);
    if (i < 0) return;
    const sw = d.swatches[i]!;
    const fix = (c: ColorRef) => {
      if (c.swatchId !== id) return;
      delete c.swatchId;
      Object.assign(c, { ...sw.color, alpha: c.alpha });
    };
    const fixPaint = (p: Paint) => {
      if (p.kind === 'solid') fix(p.color);
      else if (p.kind === 'linear' || p.kind === 'radial') p.stops.forEach((s) => fix(s.color));
    };
    const visit = (n: Node) => {
      if (n.kind === 'group') n.children.forEach(visit);
      n.appearance.fills.forEach((f) => fixPaint(f.paint));
      n.appearance.strokes.forEach((s) => fixPaint(s.paint));
    };
    for (const l of d.layers) l.children.forEach(visit);
    d.swatches.splice(i, 1);
  });

export const setColorMode = (mode: Doc['colorMode']): Command =>
  cmd('cmdColorMode', (d) => void (d.colorMode = mode));

// ── D3: paths ──────────────────────────────────────────────────────────────────────────────────────────

/** Replaces a node's outline (a live shape becomes a path the first time its anchors are edited). */
export const setSubpaths = (
  id: string,
  subpaths: Subpath[],
  merge?: string,
  fillRule?: 'nonzero' | 'evenodd',
): Command =>
  cmd(
    'cmdEditPath',
    (d) => {
      const f = findNode(d, id);
      if (!f) return;
      const n = f.node;
      if (n.kind === 'path') {
        n.subpaths = subpaths;
        if (fillRule) n.fillRule = fillRule;
      } else if (n.kind === 'shape') {
        const { shape: _s, kind: _k, ...rest } = n;
        f.siblings[f.index] = { ...rest, kind: 'path', subpaths, fillRule: fillRule ?? 'nonzero' };
      }
    },
    merge,
  );

/** A path node's subpaths after an edit, plus extra nodes (Scissors cutting one open path in two). */
export const splitPathNode = (id: string, keep: Subpath[], extra: Subpath[][], out: string[] = []): Command =>
  cmd('cmdScissors', (d) => {
    const f = findNode(d, id);
    if (!f || (f.node.kind !== 'path' && f.node.kind !== 'shape')) return;
    const base = f.node;
    const { kind: _k, ...rest } = base as Node & { shape?: unknown };
    delete (rest as { shape?: unknown }).shape;
    f.siblings[f.index] = {
      ...(rest as Omit<PathNodeT, 'kind' | 'subpaths' | 'fillRule'>),
      kind: 'path',
      subpaths: keep,
      fillRule: base.kind === 'path' ? base.fillRule : 'nonzero',
    };
    extra.forEach((sps, k) => {
      const copy: Node = {
        ...(JSON.parse(JSON.stringify(rest)) as object), // a draft cannot be structuredClone()d
        id: newId(),
        kind: 'path',
        subpaths: sps,
        fillRule: 'nonzero',
      } as Node;
      f.siblings.splice(f.index + 1 + k, 0, copy);
      out.push(copy.id);
    });
  });

/** Replaces a node by several path nodes (Knife pieces), each with the original's appearance. */
export const replaceWithPaths = (id: string, pieces: Subpath[][], out: string[] = []): Command =>
  cmd('cmdKnife', (d) => {
    const f = findNode(d, id);
    if (!f) return;
    const base = f.node;
    const nodes = pieces.map((sps, k) => ({
      id: k === 0 ? base.id : newId(),
      name: base.name,
      visible: base.visible,
      locked: base.locked,
      transform: [...base.transform] as Mat2D,
      appearance: JSON.parse(JSON.stringify(base.appearance)) as Appearance,
      kind: 'path' as const,
      subpaths: sps,
      fillRule: 'evenodd' as const,
    }));
    f.siblings.splice(f.index, 1, ...nodes);
    out.push(...nodes.map((n) => n.id));
  });

/** Make compound path (Ctrl+8): the selected paths / shapes become one path (even-odd: overlaps are holes),
 *  in the first object's coordinates, with the frontmost object's appearance. */
export const makeCompound = (ids: string[], worlds: Map<string, Mat2D>, out: string[] = []): Command =>
  cmd('cmdCompound', (d) => {
    const found = ids
      .map((id) => findNode(d, id))
      .filter((f): f is NonNullable<typeof f> => !!f && (f.node.kind === 'path' || f.node.kind === 'shape'));
    if (found.length < 2) return;
    const first = found[0]!;
    const toFirst = invMat(worlds.get(first.node.id)!);
    const subpaths: Subpath[] = [];
    for (const f of found) {
      const m = mulMat(toFirst, worlds.get(f.node.id)!);
      const sps =
        f.node.kind === 'path'
          ? f.node.subpaths
          : shapeToSubpaths((f.node as Extract<Node, { kind: 'shape' }>).shape);
      for (const sp of sps) subpaths.push(mapSubpath(sp, m));
    }
    const front = found[found.length - 1]!.node;
    const { kind: _k, ...rest } = first.node as Node & { shape?: unknown };
    delete (rest as { shape?: unknown }).shape;
    const compound = {
      ...(rest as object),
      appearance: JSON.parse(JSON.stringify(front.appearance)),
      kind: 'path',
      subpaths,
      fillRule: 'evenodd',
    } as Node;
    for (const f of [...found].reverse()) {
      const g = findNode(d, f.node.id);
      if (g) g.siblings.splice(g.index, 1);
    }
    // `first` is the backmost of the objects: the compound path takes its place in the stack.
    first.siblings.splice(Math.min(first.index, first.siblings.length), 0, compound);
    out.push(compound.id);
  });

/** Release compound path: every subpath becomes its own path. */
export const releaseCompound = (ids: string[], out: string[] = []): Command =>
  cmd('cmdReleaseCompound', (d) => {
    for (const id of ids) {
      const f = findNode(d, id);
      if (!f || f.node.kind !== 'path' || f.node.subpaths.length < 2) continue;
      const base = f.node;
      const nodes = base.subpaths.map(
        (sp, k) =>
          ({
            ...JSON.parse(JSON.stringify(base)),
            id: k === 0 ? base.id : newId(),
            subpaths: [sp],
            fillRule: 'nonzero',
          }) as Node,
      );
      f.siblings.splice(f.index, 1, ...nodes);
      out.push(...nodes.map((n) => n.id));
    }
  });

const invMat = (m: Mat2D): Mat2D => {
  const det = m[0] * m[3] - m[1] * m[2] || 1e-12;
  const a = m[3] / det,
    b = -m[1] / det,
    c = -m[2] / det,
    dd = m[0] / det;
  return [a, b, c, dd, -(a * m[4] + c * m[5]), -(b * m[4] + dd * m[5])];
};
export function mapSubpath(sp: Subpath, m: Mat2D): Subpath {
  const ap = (x: number, y: number) => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]] as const;
  return {
    closed: sp.closed,
    points: sp.points.map((p) => {
      const [x, y] = ap(p.x, p.y),
        [ix, iy] = ap(p.inX, p.inY),
        [ox, oy] = ap(p.outX, p.outY);
      return { x, y, inX: ix, inY: iy, outX: ox, outY: oy, type: p.type };
    }),
  };
}
type PathNodeT = Extract<Node, { kind: 'path' }>;

// ── D4: guides ─────────────────────────────────────────────────────────────────────────────────────────
export const addGuide = (axis: 'x' | 'y', pos: number, out: string[] = []): Command =>
  cmd('cmdGuide', (d) => {
    const id = newId('gd');
    d.guides.push({ id, axis, pos });
    out.push(id);
  });
export const moveGuide = (id: string, pos: number, merge?: string): Command =>
  cmd(
    'cmdGuide',
    (d) => {
      const g = d.guides.find((x) => x.id === id);
      if (g) g.pos = pos;
    },
    merge,
  );
export const removeGuide = (id: string, merge?: string): Command =>
  cmd(
    'cmdGuideDelete',
    (d) => {
      const i = d.guides.findIndex((x) => x.id === id);
      if (i >= 0) d.guides.splice(i, 1);
    },
    merge,
  );
